import { TrendTab } from "@core/constants";
import { Metric } from "@core/models/Metric";
import { FCX } from "@datassential/platform-ui-lib";
import { createContext, Dispatch, SetStateAction, useContext, useMemo, useState } from "react";

type TrendContextType = {
    selectedMetric: Metric;
    setSelectedMetric: (metric: Metric) => void;
    activeTab: TrendTab;
    setActiveTab: (tab: TrendTab) => void;
    search: string;
    setSearch: (search: string) => void;
    isMounted: boolean;
    setIsMounted: Dispatch<SetStateAction<boolean>>;
};

const defaultContext: TrendContextType = {
    selectedMetric: Metric.LoveIt,
    setSelectedMetric: () => {},
    activeTab: TrendTab.HistoricTrend,
    setActiveTab: () => {},
    search: '',
    setSearch: () => {},
    isMounted: false,
    setIsMounted: () => {},
};

const TrendContext = createContext<TrendContextType>(defaultContext);

export const useTrendContext = () => {
    const context = useContext(TrendContext);
    if (!context) throw new Error("Trend context is not initialized");
    return context;
};

const TrendContextProvider: FCX = ({ children }) => {
    const [selectedMetric, setSelectedMetric] = useState<TrendContextType['selectedMetric']>(defaultContext.selectedMetric);
    const [activeTab, setActiveTab] = useState<TrendContextType['activeTab']>(defaultContext.activeTab);
    const [search, setSearch] = useState<TrendContextType['search']>(defaultContext.search);
    const [isMounted, setIsMounted] = useState<TrendContextType['isMounted']>(defaultContext.isMounted);

    const value = useMemo(() => ({
        selectedMetric,
        setSelectedMetric,
        activeTab,
        setActiveTab,
        search,
        setSearch,
        isMounted,
        setIsMounted,
    }), [
        selectedMetric,
        setSelectedMetric,
        activeTab,
        setActiveTab,
        search,
        setSearch,
        isMounted,
        setIsMounted,
    ]);

    return (
        <TrendContext.Provider value={value}>
            {children}
        </TrendContext.Provider>
    );
};

export default TrendContextProvider;